import { StatusChip } from './StatusChip';
import type { Tool } from './tools';

type Props = {
  tool: Tool;
  accent: string;
  onLaunch: (tool: Tool) => void;
  hasAccess: boolean;
};

export function ToolCardMono({ tool, accent, onLaunch, hasAccess }: Props) {
  const isFeatured = !!tool.featured;
  const comingSoon = tool.status === 'soon';
  const locked = !hasAccess && !comingSoon;
  const disabled = comingSoon || locked;
  const featureAccent = tool.accent ?? accent;

  const buttonLabel = comingSoon ? 'Notify me' : locked ? 'No access' : 'Launch';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        borderRadius: 12,
        overflow: 'hidden',
        border: isFeatured ? `1.5px solid ${featureAccent}` : '1px solid rgba(14,14,14,.10)',
        position: 'relative',
        boxShadow: isFeatured ? `0 2px 0 ${featureAccent}1a` : 'none',
        opacity: disabled ? 0.85 : 1,
        transition: 'transform .2s, box-shadow .2s',
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 14px 30px rgba(14,14,14,.08)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = isFeatured ? `0 2px 0 ${featureAccent}1a` : 'none';
      }}
    >
      {isFeatured && (
        <span
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            zIndex: 2,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: featureAccent,
          }}
        >
          Core platform
        </span>
      )}

      <div
        style={{
          background: 'var(--ink)',
          color: '#fff',
          height: 168,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-start',
          padding: 18,
        }}
      >
        <span
          style={{
            fontSize: 104,
            fontWeight: 700,
            lineHeight: 0.85,
            letterSpacing: '-0.04em',
            color: disabled ? 'rgba(255,255,255,.35)' : '#fff',
          }}
        >
          {tool.mono}
        </span>
        {isFeatured && (
          <svg
            viewBox="0 0 200 200"
            style={{ position: 'absolute', inset: 0, opacity: 0.55, mixBlendMode: 'screen' }}
            aria-hidden
          >
            <path
              d="M -20 150 C 50 40, 150 40, 220 150"
              fill="none"
              stroke={featureAccent}
              strokeWidth="1.2"
              opacity=".55"
            />
            <path
              d="M -20 175 C 50 70, 150 70, 220 175"
              fill="none"
              stroke={featureAccent}
              strokeWidth="1.2"
              opacity=".4"
            />
          </svg>
        )}
      </div>

      <div
        style={{
          padding: '18px 18px 16px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 10,
          }}
        >
          <div>
            <h3 className="h3" style={{ fontSize: 20 }}>
              {tool.name}
            </h3>
            <p
              className="body"
              style={{ margin: '2px 0 0', fontSize: 13, color: 'rgba(14,14,14,.62)' }}
            >
              {tool.tagline}
            </p>
          </div>
          <StatusChip status={tool.status} />
        </div>
        <p
          className="body"
          style={{ margin: 0, fontSize: 13.5, color: 'rgba(14,14,14,.78)', flex: 1 }}
        >
          {tool.desc}
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 6,
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <span
              style={{
                fontSize: 10,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'rgba(14,14,14,.55)',
                fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              }}
            >
              {tool.audience.join(' · ')}
            </span>
            <span className="mono" style={{ color: 'rgba(14,14,14,.45)' }}>
              Updated {tool.updated}
            </span>
          </div>
          <button
            onClick={() => !disabled && onLaunch(tool)}
            disabled={disabled}
            title={locked ? 'You don’t have a role that grants access to this tool.' : undefined}
            style={{
              border: 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
              background: disabled ? 'rgba(14,14,14,.05)' : 'var(--ink)',
              color: disabled ? 'rgba(14,14,14,.45)' : '#fff',
              padding: '8px 14px',
              borderRadius: 999,
              fontFamily: 'inherit',
              fontWeight: 500,
              fontSize: 12.5,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {buttonLabel}
            {!disabled && (
              <svg width="11" height="11" viewBox="0 0 11 11" aria-hidden>
                <path
                  d="M2 9 L9 2 M4 2 L9 2 L9 7"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
